/**
 * When site maintenance mode is on, only super-admin and admin may use the app.
 * Others see the maintenance page. The landing page is hidden. Enrollment is suspended.
 * Parent sign-in is blocked (API + login page). /login stays available for admins.
 *
 * Uses PublicSiteSettingsProvider (session cache + GET /site-settings) so maintenance
 * state is known on first paint when settings were prefetched or cached — no late layout jump.
 * Other tabs reload via BroadcastChannel when maintenance is toggled.
 */

import { useEffect, useRef, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

import { MAINTENANCE_MODE_BROADCAST_CHANNEL } from '@constants/maintenanceMode';
import { useAuth } from '@contexts/AuthContext';
import { usePublicSiteSettings } from '@contexts/PublicSiteSettingsContext';
import { MaintenancePage } from '@pages/MaintenancePage';
import { reloadForMaintenanceModeChange } from '@utils/maintenanceModeSync';
import type { MaintenanceModeBroadcastMessage } from '@utils/maintenanceModeSync';
import { isAdminOrSuperAdminRole } from '@utils/roles';
import { resolveMaintenanceSupportEmail } from '@lambda/shared/directorEmail';
import { isPathAvailableDuringMaintenance } from '@utils/siteMaintenanceMode';

import { MaintenanceModeAdminBanner } from './MaintenanceModeAdminBanner';

export function MaintenanceGate({ children }: { children: ReactNode }) {
  const { user, isRestored } = useAuth();
  const { settings } = usePublicSiteSettings();
  const location = useLocation();

  /** Last known maintenance flag; used to detect toggles and force reload. */
  const knownMaintenanceRef = useRef<boolean | null>(null);

  const maintenanceOn = settings?.siteMaintenanceMode === true;
  const maintenanceMessage = settings?.maintenanceMessage;
  const supportEmail = settings ? resolveMaintenanceSupportEmail(settings) : undefined;

  useEffect(() => {
    if (settings == null) return;

    const on = maintenanceOn;
    if (knownMaintenanceRef.current !== null && knownMaintenanceRef.current !== on) {
      reloadForMaintenanceModeChange();
      return;
    }
    knownMaintenanceRef.current = on;
  }, [settings, maintenanceOn]);

  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return;
    const channel = new BroadcastChannel(MAINTENANCE_MODE_BROADCAST_CHANNEL);
    channel.onmessage = (event: MessageEvent<MaintenanceModeBroadcastMessage>) => {
      if (event.data?.type === 'maintenance-changed') {
        reloadForMaintenanceModeChange();
      }
    };
    return () => channel.close();
  }, []);

  const path = location.pathname;

  if (!maintenanceOn) {
    return <>{children}</>;
  }

  if (isAdminOrSuperAdminRole(user?.role)) {
    return (
      <>
        <MaintenanceModeAdminBanner />
        <div className="bhg-maintenance-admin-shell bhg-maintenance-admin-shell--banner">
          {children}
        </div>
      </>
    );
  }

  // Auth restores stored user on first tick — wait so admins are not flashed the public page.
  if (!isRestored) {
    return <>{children}</>;
  }

  if (isPathAvailableDuringMaintenance(path)) {
    return <>{children}</>;
  }

  return <MaintenancePage message={maintenanceMessage} supportEmail={supportEmail} />;
}
